import { useMemo } from "react";
import { LinkProps } from "./types";
import { styles } from "./styles";

/**
 * `Link` component is used for navigating (and sharing state) between routes.
 *
 * If `window.useNavigate` is available,
 * it's used to navigate when the link is clicked.
 * Otherwise, it fallsback to a regular `<a>` tag.
 *
 * ### Usage
 * `to` prop to specify the target route
 * ```tsx
 * <Link to="/here">go here</Link>
 * ```
 *
 * `state` prop to specify the state to be passed to the target route
 * ```tsx
 * <Link
 *  to="/there"
 *  state={{ some: "things" }}
 * >
 *  go there
 * </Link>
 *
 *```
 */
const Link = ({ to, state, onClick, disabled, ...props }: LinkProps) => {
  const navigate = window.RouterUtilsKit?.useNavigate?.();

  const isUseNavigate = typeof navigate === "function";

  const href = useMemo(() => {
    if (isUseNavigate) {
      return "#";
    }
    if (typeof to === "string") {
      return to;
    }
    if (typeof to === "object") {
      const { pathname, search = "", hash = "" } = to;
      return `${pathname}${search}${hash}`;
    }
    return "#";
  }, [isUseNavigate, to]);

  function handleOnClick(e: React.MouseEvent<HTMLAnchorElement>) {
    if (disabled) {
      e.preventDefault();
      return;
    }

    onClick?.(e);
    if (e.isDefaultPrevented()) return;

    if (isUseNavigate) {
      e.preventDefault();
      if (typeof state === "undefined") {
        navigate(to);
      } else {
        navigate(to, { state });
      }
    }
  }

  return (
    <a
      {...props}
      href={href}
      onClick={handleOnClick}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : props.tabIndex}
      css={disabled && styles.disabled}
    />
  );
};

export default Link;
